import { RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';
import type { DateRangePreset } from '../../types/analytics.types';
import { DATE_RANGE_PRESETS } from '../../types/analytics.types';
import { RefreshIndicator } from '../analytics/RefreshIndicator';

const PRESET_LABELS: Record<string, string> = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  '12m': '12m',
};

type EngagementMetricsControlBarProps = {
  dateRange: DateRangePreset;
  onDateRangeChange: (preset: DateRangePreset) => void;
  lastRefreshed: string | null;
  onRefresh: () => void;
  isRefreshing: boolean;
  canRefresh: boolean;
};

export const EngagementMetricsControlBar = ({
  dateRange,
  onDateRangeChange,
  lastRefreshed,
  onRefresh,
  isRefreshing,
  canRefresh,
}: EngagementMetricsControlBarProps) => {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap px-4 lg:px-6 py-3 border-b border-gray-200 bg-white">
      <div className="flex items-center gap-3 flex-wrap">
        <div
          className="flex rounded-md border border-gray-300 overflow-hidden"
          role="group"
          aria-label="Date range"
        >
          {DATE_RANGE_PRESETS.filter((preset) => preset !== 'custom').map(
            (preset) => (
              <button
                key={preset}
                data-active={dateRange === preset ? 'true' : 'false'}
                onClick={() => onDateRangeChange(preset)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  dateRange === preset
                    ? 'bg-[#15543e] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50',
                )}
              >
                {PRESET_LABELS[preset] ?? preset}
              </button>
            ),
          )}
        </div>

        <RefreshIndicator lastRefreshed={lastRefreshed} isRefreshing={isRefreshing} />
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={!canRefresh}
        aria-label="Refresh data"
      >
        {isRefreshing ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4 mr-2" />
        )}
        {isRefreshing ? 'Refreshing...' : 'Refresh'}
      </Button>
    </div>
  );
};
